import React from "react";
import { ChatSession } from "../services/decisionSupport";
import "./DecisionSupportSidebar.css";

// Export analysis types for use in other components
export enum AnalysisType {
  CLINICAL_DECISION_SUPPORT = "clinical_decision_support",
  PROGRESS_SUMMARY = "progress_summary",
  GOAL_DEVELOPMENT = "goal_development",
  GENERALIZATION_PLANS = "generalization_plans",
  BARRIER_ANALYSIS = "barrier_analysis",
  PARENT_TRAINING = "parent_training",
  DISCHARGE_SUMMARY = "discharge_summary",
  SAFETY_PLANS = "safety_plans",
  INTERDISCIPLINARY_NOTES = "interdisciplinary_notes",
}

export interface AnalysisTypeInfo {
  id: AnalysisType;
  name: string;
  icon: string;
  description: string;
}

export const analysisTypes: AnalysisTypeInfo[] = [
  {
    id: AnalysisType.CLINICAL_DECISION_SUPPORT,
    name: "Clinical Decision Support",
    icon: "🩺",
    description: "Comprehensive AI-powered clinical analysis",
  },
  {
    id: AnalysisType.PROGRESS_SUMMARY,
    name: "Progress Summary",
    icon: "📊",
    description: "Analyze client progress and outcomes",
  },
  {
    id: AnalysisType.GOAL_DEVELOPMENT,
    name: "Goal Development",
    icon: "🎯",
    description: "Generate and refine treatment goals",
  },
  {
    id: AnalysisType.GENERALIZATION_PLANS,
    name: "Generalization Plans",
    icon: "🔄",
    description: "Create skill generalization strategies",
  },
  {
    id: AnalysisType.BARRIER_ANALYSIS,
    name: "Barrier Analysis",
    icon: "🚧",
    description: "Identify and address treatment barriers",
  },
  {
    id: AnalysisType.PARENT_TRAINING,
    name: "Parent Training Recommendations",
    icon: "👨‍👩‍👧",
    description: "Generate parent training suggestions",
  },
  {
    id: AnalysisType.DISCHARGE_SUMMARY,
    name: "Discharge Summary",
    icon: "📝",
    description: "Create comprehensive discharge documentation",
  },
  {
    id: AnalysisType.SAFETY_PLANS,
    name: "Safety Plans",
    icon: "🛡️",
    description: "Develop safety and crisis intervention plans",
  },
  {
    id: AnalysisType.INTERDISCIPLINARY_NOTES,
    name: "Interdisciplinary Collaboration Notes",
    icon: "🤝",
    description: "Generate team collaboration documentation",
  },
];

// Get icon for analysis type
export function getAnalysisTypeIcon(analysisType: string): string {
  const type = analysisTypes.find((t) => t.id === analysisType);
  return type?.icon || "💬";
}

interface DecisionSupportSidebarProps {
  sessions: ChatSession[];
  activeSessionId: number | null;
  onSessionSelect: (sessionId: number) => void;
  onNewSession: () => void;
  onDeleteSession: (sessionId: number) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isLoading?: boolean;
}

const DecisionSupportSidebar: React.FC<DecisionSupportSidebarProps> = ({
  sessions,
  activeSessionId,
  onSessionSelect,
  onNewSession,
  onDeleteSession,
  isCollapsed,
  onToggleCollapse,
  isLoading = false,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <aside
      className={`decision-support-sidebar ${
        isCollapsed ? "decision-support-sidebar--collapsed" : ""
      }`}
    >
      <div className="decision-support-sidebar__header">
        <h2 className="decision-support-sidebar__title">
          {!isCollapsed && <span>Chat Sessions</span>}
        </h2>
        <button
          className="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? "→" : "←"}
        </button>
      </div>

      {!isCollapsed && (
        <>
          <div className="decision-support-sidebar__actions">
            <button
              className="new-session-btn"
              onClick={onNewSession}
              disabled={isLoading}
            >
              New Session
            </button>
          </div>

          <div className="decision-support-sidebar__sessions">
            {isLoading ? (
              <div className="sessions-loading">
                <div className="spinner"></div>
                <p>Loading sessions...</p>
              </div>
            ) : sessions.length === 0 ? (
              <div className="sessions-empty">
                <span className="sessions-empty__icon">💬</span>
                <p>No sessions yet</p>
                <p className="sessions-empty__hint">
                  Click "New Session" to start
                </p>
              </div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id}
                  className={`session-item ${
                    activeSessionId === session.id ? "session-item--active" : ""
                  }`}
                  onClick={() => onSessionSelect(session.id)}
                >
                  <div className="session-item__header">
                    <span className="session-item__icon">
                      {getAnalysisTypeIcon(session.analysis_type)}
                    </span>
                    <div className="session-item__info">
                      <div className="session-item__title">{session.title}</div>
                      <div className="session-item__meta">
                        <span className="session-item__time">
                          {formatDate(session.last_message_at)}
                        </span>
                        <span className="session-item__separator">•</span>
                        <span className="session-item__messages">
                          {session.message_count} msg
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="session-item__delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          `Delete "${session.title}"? This cannot be undone.`
                        )
                      ) {
                        onDeleteSession(session.id);
                      }
                    }}
                    title="Delete session"
                  >
                    🗑️
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </aside>
  );
};

export default DecisionSupportSidebar;
